import { getLogger } from '../../diagnostics/logger';
import moment from 'moment';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  ENSURE_ORANGE_PROVISIONING_ORDER,
  ENSURE_PROVISIONING,
  EXPORT_SIMS,
  INITIAL_AUTHENTICATION,
  LOAD_ORANGE_PROVISIONING_ORDER,
  LOAD_PROVISIONING,
  LOAD_SIMS,
  LOAD_SIMS_IF_NEEDED
} from '../../actions/actionTypes';
import { Action, PromiseAction } from '../../actions/commonActions';
import { addNotification } from '../../actions/notificationActions';
import {
  loadBucketFailure,
  loadBucketSuccess,
  loadProvisioningFailure,
  loadProvisioningPending,
  loadProvisioningSuccess,
  loadSimsFailure,
  loadSimsPending,
  loadSimsSuccess
} from '../../actions/simActions';
import * as mvnoBundleApi from '../../api/mvnoBundleApi';
import * as provisioningOrderApi from '../../api/orangeProvisioningOrderApi';
import * as simApi from '../../api/simApi';
import * as provisioningApi from '../../api/simProvisionApi';
import { SimSearchOptions } from '../../api/utilities';
import { HashMap } from '../../constants';
import { SimManagementError } from '../../models/Errors';
import { ExportSimParameters } from '../../models/account';
import { Provisioning } from '../../models/provision';
import { QueryResult } from '../../models/queryResult';
import { BasicPageableSearchFilter } from '../../models/searchFilter';
import {
  LoadBucketSuccessResponse,
  LoadProvisioningSuccessResponse,
  Sim,
  SimInfo,
  SimsSuccessResponse
} from '../../models/sim';
import { ProductOrder } from '../../models/state/bucket';
import { SimsState } from '../../models/state/simsState';
import { UserSettings } from '../../models/state/userSettings';
import { selectSelectedSims, selectSims } from '../../selectors/sims';
import { selectUserSettings } from '../../selectors/userSettings';
import { exportSimsTable } from '../../services/helpers';
import { AppState } from 'src/models/state/appState';

const log = getLogger('simmanagement.sagas.login');

/*
 * A saga which fetches the list of SIMS
 */
export function* loadSimsSaga(action: PromiseAction<BasicPageableSearchFilter, {}>) {
  const searchFilter = action.payload;
  if (!searchFilter) {
    return;
  }

  yield put(loadSimsPending(searchFilter));

  try {
    const searchOptions: Partial<SimSearchOptions> = {
      filters: searchFilter.filters,
      sortModel: searchFilter.sortModel,
      accountId: searchFilter.accountId,
      skip: searchFilter.startIndex,
      wrapWith: {
        totalCount: true
      }
    };

    if (searchFilter.stopIndex) {
      searchOptions.take = searchFilter.stopIndex - searchFilter.startIndex + 1;
    }

    if (searchFilter.getAll) {
      searchOptions.skip = 0;
      searchOptions.take = 250;
    }

    let simsFromApi: QueryResult<Sim> = yield call(simApi.get, searchFilter.companyId, searchOptions);

    if (searchFilter.getAll) {
      while ((simsFromApi.count ?? 0) < (simsFromApi.totalCount ?? 0)) {
        searchOptions.skip = simsFromApi.count ?? 0;
        const _simsFromApi: QueryResult<Sim> = yield call(simApi.get, searchFilter.companyId, searchOptions);
        simsFromApi.count = (simsFromApi.count ?? 0) + (_simsFromApi.count ?? 0);
        simsFromApi.result = [...simsFromApi.result, ..._simsFromApi.result];
      }
    }

    yield put(
      loadSimsSuccess({
        searchFilter,
        sims: simsFromApi
      })
    );

    const response: SimsSuccessResponse = {
      ...searchOptions,
      totalCount: simsFromApi.totalCount,
      searchFilter,
      sims: simsFromApi
    };

    action.resolve && action.resolve(response);
  } catch (error: any) {
    yield put(loadSimsFailure(searchFilter, error));
    action.reject && action.reject(error);
    console.error(error);
  }
}

export function* loadProvisioningSaga(action: PromiseAction<number, {}>) {
  const stockId = action.payload;
  if (!stockId) {
    return;
  }

  const selectedSims: HashMap<SimInfo> = yield select(selectSelectedSims);
  const sim = selectedSims[stockId];
  if (!sim) {
    return;
  }

  yield put(loadProvisioningPending(stockId));
  const msisdn = sim.tel;

  try {
    const provisioningFromApi: Provisioning = yield call(provisioningApi.getProvision, msisdn);
    const response: LoadProvisioningSuccessResponse = {
      stockId,
      provisioning: provisioningFromApi
    };

    yield put(loadProvisioningSuccess(response));
    action.resolve && action.resolve(response);
  } catch (error: any) {
    yield put(loadProvisioningFailure(stockId, error));
    console.error(error);
  }
}

/**
 * A saga which fetches the SIM bucket details if required.
 * @param action
 */
export function* ensureBucketSaga(action: Action<number>) {
  const payload = action.payload;
  if (!payload) {
    return;
  }

  const selectedSims: HashMap<SimInfo> = yield select(selectSelectedSims);
  const sim = selectedSims[payload];
  if (
    sim &&
    (!sim.orangeProductOrder || (sim.orangeProductOrder.expiresOn && sim.orangeProductOrder.expiresOn < new Date()))
  ) {
    yield call(loadOrangeProvisioningOrderSaga, action);
  }
}

export function* loadOrangeProvisioningOrderSaga(action: PromiseAction<number, {}>) {
  const stockId = action.payload;
  if (!stockId) {
    return;
  }

  const selectedSims: HashMap<SimInfo> = yield select(selectSelectedSims);
  const sim = selectedSims[stockId];
  if (!sim) {
    return;
  }

  yield put(loadProvisioningPending(stockId));
  try {
    const bucketFromApi: ProductOrder = yield call(provisioningOrderApi.get, sim.serialNumber);
    const response: LoadBucketSuccessResponse = {
      stockId,
      productOrder: bucketFromApi
    };

    yield put(loadBucketSuccess(response));
    action.resolve && action.resolve(response);
  } catch (error: any) {
    yield put(loadBucketFailure(stockId, error));
    console.error(error);
  }
}

export function* loadMvnoSaga(action: PromiseAction<number, {}>) {
  const stockId = action.payload;
  if (!stockId) {
    return;
  }

  const selectedSims: HashMap<SimInfo> = yield select(selectSelectedSims);
  const sim = selectedSims[stockId];
  if (!sim) {
    return;
  }

  try {
    const bucketFromApi: ProductOrder = yield call(mvnoBundleApi.getBundle, sim.serialNumber);
    const response: LoadBucketSuccessResponse = {
      stockId,
      productOrder: bucketFromApi
    };

    yield put(loadBucketSuccess(response));
    action.resolve && action.resolve(response);
  } catch (error: any) {
    yield put(loadBucketFailure(stockId, error));
    console.error(error);
  }
}

/**
 * A saga which fetches the SIM provisioning details if required.
 * @param action
 */
export function* ensureProvisioningSaga(action: Action<number>) {
  const payload = action.payload;
  if (!payload) {
    return;
  }

  const selectedSims: HashMap<SimInfo> = yield select(selectSelectedSims);
  const sim = selectedSims[payload];
  if (sim && (!sim.provisioning || (sim.provisioning.expiresOn && sim.provisioning.expiresOn < new Date()))) {
    yield call(loadProvisioningSaga, action);
  }
}

/*
 * A saga which fetches the list of SIMs if required.
 */
export function* loadSimsIfNeededSaga(action: Action<BasicPageableSearchFilter>) {
  const payload = action.payload;
  if (!payload) {
    return;
  }

  const simsState: SimsState = yield select(selectSims);
  const { companyId, accountId } = simsState.searchFilter;
  if (
    companyId !== payload.companyId ||
    accountId !== payload.accountId ||
    (simsState.expiresOn && simsState.expiresOn < moment().toDate()) ||
    simsState.searchFilter.filters !== payload.filters
  ) {
    yield call(loadSimsSaga, action);
  }
}

export function* exportSimsSaga(action: Action<ExportSimParameters>): Generator<any, void, any> {
  if (!action.payload) {
    log.warn(`Attempted to process action ${action.type} without payload. Ignoring.`);
    return;
  }

  try {
    const userSettings: UserSettings = yield select(selectUserSettings);

    let sims: Array<Sim> = [];

    if (action.payload.exportSelected) {
      const selectedSims = yield select(selectSelectedSims);
      sims = Object.values(selectedSims);
    } else {
      sims = yield call(simApi.exportSims, action.payload);
    }

    const subportalUser = yield select((state: AppState) => state.loginDetails.user?.subportalUser);
    yield call(exportSimsTable, sims, userSettings.userSettings, subportalUser);
  } catch (err: any) {
    const error = err as SimManagementError;
    if (error.message) {
      log.error(error.message);
    }

    let errorMessage = '';

    try {
      error.getStandardHttpError();
    } catch {}

    if (!errorMessage) {
      errorMessage = 'an error occurred';
    }

    yield put(
      addNotification({
        category: 'task',
        title: 'Error',
        detail: `Export failed - ${errorMessage}.`,
        type: 'error',
        showInCount: false
      })
    );
  }
}

/*
 * Initializes all sagas that are related to SIMS.
 */
export function* initSimSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(ENSURE_ORANGE_PROVISIONING_ORDER, ensureBucketSaga);
  yield takeEvery(ENSURE_PROVISIONING, ensureProvisioningSaga);
  yield takeEvery(EXPORT_SIMS, exportSimsSaga);
  yield takeEvery(LOAD_ORANGE_PROVISIONING_ORDER, loadOrangeProvisioningOrderSaga);
  yield takeEvery(LOAD_PROVISIONING, loadProvisioningSaga);
  yield takeEvery(LOAD_SIMS, loadSimsSaga);
  yield takeEvery(LOAD_SIMS_IF_NEEDED, loadSimsIfNeededSaga);
}
